import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import QuoteBuilderPage from "./pages/QuoteBuilder";
import QuoteViewPage from "./pages/QuoteViewPage";
import QuotePaymentSuccessPage from "./pages/QuotePaymentSuccessPage";
import QuoteResponsePage from "./pages/QuoteResponsePage";
import ChangeOrderViewPage from "./pages/ChangeOrderViewPage";
import ChangeOrderPaymentSuccessPage from "./pages/ChangeOrderPaymentSuccessPage";
import ChangeOrderBuilderPage from "./pages/ChangeOrderBuilderPage";
import QuoteActivityPage from "./pages/QuoteActivityPage";
import ProposalsPage from "./pages/ProposalsPage";
import ProposalBuilderPage from "./pages/ProposalBuilderPage";
import ProposalSettingsPage from "./pages/ProposalSettingsPage";
import CustomersPage from "./pages/CustomersPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import SupportPage from "./pages/SupportPage";
import EquipmentPage from "./pages/EquipmentPage";
import ServicesPage from "./pages/ServicesPage";
import WebhooksPage from "./pages/WebhooksPage";
import EquipmentLogViewPage from "./pages/EquipmentLogViewPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "landclearco_token";

// NOTE: Auth token is stored in localStorage rather than httpOnly cookies.
// httpOnly cookies were attempted but fail in this Kubernetes proxy environment
// due to SameSite/CORS constraints across the ingress boundary.
// XSS risk is mitigated by the DOMPurify sanitization on all user-rendered HTML.

// Attach Bearer token to every axios request
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        await axios.get(`${API}/auth/verify`);
        setIsAuthenticated(true);
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = (token) => {
    localStorage.setItem(TOKEN_KEY, token);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00341d]"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Public privacy policy (App Store requirement) */}
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          {/* Public support page (App Store requirement) */}
          <Route path="/support" element={<SupportPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/new"
            element={
              <ProtectedRoute>
                <QuoteBuilderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/:quoteId/edit"
            element={
              <ProtectedRoute>
                <QuoteBuilderPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/quotes/:quoteId/change-orders/new"
            element={
              <ProtectedRoute>
                <ChangeOrderBuilderPage />
              </ProtectedRoute>
            }
          />
          {/* Public quote view for customers */}
          <Route path="/quote/:quoteId" element={<QuoteViewPage />} />
          <Route path="/quote/:quoteId/payment-success" element={<QuotePaymentSuccessPage />} />
          <Route path="/quote/:quoteId/response" element={<QuoteResponsePage />} />
          <Route
            path="/quotes/:quoteId/activity"
            element={<ProtectedRoute><QuoteActivityPage /></ProtectedRoute>}
          />
          {/* Public change order view for customers */}
          <Route path="/change-order/:changeOrderId" element={<ChangeOrderViewPage />} />
          <Route path="/change-order/:changeOrderId/payment-success" element={<ChangeOrderPaymentSuccessPage />} />

          {/* Proposals */}
          <Route path="/proposals" element={<ProtectedRoute><ProposalsPage /></ProtectedRoute>} />
          <Route path="/proposals/:proposalId/edit" element={<ProtectedRoute><ProposalBuilderPage /></ProtectedRoute>} />
          <Route path="/proposal-settings" element={<ProtectedRoute><ProposalSettingsPage /></ProtectedRoute>} />

          {/* Customers */}
          <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />

          {/* Equipment Logs */}
          <Route path="/equipment" element={<ProtectedRoute><EquipmentPage /></ProtectedRoute>} />
          <Route path="/services" element={<ProtectedRoute><ServicesPage /></ProtectedRoute>} />
          <Route path="/webhooks" element={<ProtectedRoute><WebhooksPage /></ProtectedRoute>} />
          {/* Public log view (used by the calendar reminder link) */}
          <Route path="/equipment-logs/:logId" element={<EquipmentLogViewPage />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
